import { useCallback, useState, useSyncExternalStore } from 'react';

/**
 * What the player picked on an offer list - the sport on the live rail, the
 * market a sport's rows show - kept on the device for a while, so a refresh,
 * a match opened and closed, the slip or a reload leave it as it was (owner,
 * 2026-11-05: "da ostane ovo 1X2 ... da ne mora da se vraćamo").
 *
 * localStorage, not sessionStorage: an iPhone home-screen app starts a new
 * session each time it is opened. A choice older than CHOICE_MAX_AGE_MS is
 * forgotten: the next day the lists open on their defaults again.
 */
export const CHOICE_MAX_AGE_MS = 12 * 60 * 60 * 1000;
const PREFIX = 'offer-choice:';

type Saved = { v: string; at: number };

/** A stored choice, or null when there is none or it is too old. */
export const parseChoice = (
  raw: string | null,
  now: number,
  maxAgeMs = CHOICE_MAX_AGE_MS
): string | null => {
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw) as Partial<Saved> | null;
    if (
      !saved ||
      typeof saved.v !== 'string' ||
      typeof saved.at !== 'number' ||
      now - saved.at > maxAgeMs ||
      saved.at > now + 60_000
    )
      return null;
    return saved.v;
  } catch {
    return null;
  }
};

export const serializeChoice = (value: string, now: number) => {
  const saved: Saved = { v: value, at: now };
  return JSON.stringify(saved);
};

// Read once per key; renders (every live update) read the copy in memory.
const cache = new Map<string, string | null>();
const listeners = new Set<() => void>();

export const readChoice = (key: string): string | null => {
  if (cache.has(key)) return cache.get(key)!;
  let value: string | null = null;
  try {
    value = parseChoice(localStorage.getItem(PREFIX + key), Date.now());
  } catch {
    // Storage blocked: the choice lasts while the page is open.
  }
  cache.set(key, value);
  return value;
};

export const writeChoice = (key: string, value: string) => {
  if (cache.get(key) === value) {
    // Picked again: only its age is renewed.
  } else {
    cache.set(key, value);
    listeners.forEach((l) => l());
  }
  try {
    localStorage.setItem(PREFIX + key, serializeChoice(value, Date.now()));
  } catch {
    // A convenience only.
  }
};

/** Tests: forget what was read. */
export const resetChoiceCache = () => cache.clear();

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

/**
 * A remembered choice as state. Every component with the same key shows the
 * same value; a null key is plain state that is not kept.
 */
export function useRememberedChoice(
  key: string | null,
  fallback: string
): [string, (value: string) => void] {
  const [local, setLocal] = useState(fallback);
  const stored = useSyncExternalStore(subscribe, () =>
    key ? readChoice(key) : null
  );
  const set = useCallback(
    (value: string) => (key ? writeChoice(key, value) : setLocal(value)),
    [key]
  );
  return [key ? stored ?? fallback : local, set];
}
